import React, { useState, useEffect } from 'react';
import api from '../../../utils/api';
import { Search, MapPin, Download, CheckSquare, Square, Star, Map, Globe, Loader2, Check, X, User as UserIcon, Thermometer } from 'lucide-react';
import LeadScraperSettings from '../SuperAdmin/LeadScraperSettings';

const LeadScraperShared = ({ showSettings = false }) => {
  const [query, setQuery] = useState('');
  const [location, setLocation] = useState('');
  const [maxResults, setMaxResults] = useState(20);
  
  const [status, setStatus] = useState('idle');
  const [results, setResults] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  
  const [importing, setImporting] = useState(false);
  const [importMessage, setImportMessage] = useState('');
  const [error, setError] = useState('');

  const [showImportModal, setShowImportModal] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [leadTypes, setLeadTypes] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedLeadType, setSelectedLeadType] = useState('');
  const [leadSources, setLeadSources] = useState([]);
  const [selectedLeadSource, setSelectedLeadSource] = useState('');
  const [loadingData, setLoadingData] = useState(false);

  useEffect(() => {
    let interval;

    const fetchCache = async () => {
      try {
        const res = await api('/lead-scrape/cache');
        if (res.ok) {
          const data = await res.json();
          
          setStatus(data.status || 'idle');
          if (data.status === 'error') {
            if (data.error === 'NO_API_KEY') {
               setError('Please configure your Apify API Token in settings first.');
            } else {
               setError(data.error || 'Failed to scrape leads.');
            }
          } else {
            setError('');
          }

          if (data.results && data.results.length > 0) {
            setResults(data.results);
          } else if (data.status !== 'scraping') {
            setResults([]);
          }

          if (data.query) setQuery(data.query);
          if (data.location) setLocation(data.location);
          if (data.maxResults) setMaxResults(data.maxResults);
        }
      } catch (err) {
        console.error('Failed to fetch lead scraper cache', err);
      }
    };
    
    // Always fetch immediately when effect runs or status changes to check if we should keep polling
    fetchCache();

    if (status === 'scraping') {
      interval = setInterval(fetchCache, 3000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [status]);

  useEffect(() => {
    if (showImportModal && employees.length === 0) {
      const fetchData = async () => {
        setLoadingData(true);
        try {
          const [usersRes, typesRes, sourcesRes] = await Promise.all([
            api('/users'),
            api('/lead-types'),
            api('/lead-sources')
          ]);
          if (usersRes.ok) {
            const users = await usersRes.json();
            setEmployees(users.filter(u => u.role !== 'super-admin'));
          }
          if (typesRes.ok) {
            const types = await typesRes.json();
            setLeadTypes(types);
          }
          if (sourcesRes.ok) {
            const sources = await sourcesRes.json();
            setLeadSources(sources);
          }
        } catch (err) {
          console.error('Error fetching data for modal', err);
        } finally {
          setLoadingData(false);
        }
      };
      fetchData();
    }
  }, [showImportModal, employees.length]);

  const handleScrape = async (e) => {
    e.preventDefault();
    if (!query || !location) return;
    
    setStatus('scraping');
    setError('');
    setResults([]);
    setSelectedIds(new Set());
    setImportMessage('');

    try {
      const res = await api('/lead-scrape/scrape', {
        method: 'POST',
        body: { query, location, maxResults }
      });
      const data = await res.json();
      
      if (!res.ok) {
        throw { response: { data } };
      }
      
      // Polling will handle fetching the results
    } catch (err) {
      setStatus('error');
      if (err.response?.data?.message === 'NO_API_KEY') {
        setError('Please configure your Apify API Token in settings first.');
      } else {
        setError('Failed to start scraping. Please check your query or API key.');
      }
    }
  };

  const toggleSelect = (id) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === results.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(results.map((r, index) => r._id || r.placeId || `index-${index}`)));
    }
  };

  const handleImport = async (skipOptional = false) => {
    if (selectedIds.size === 0) return;
    setImporting(true);
    setImportMessage('');

    const selectedLeads = results.filter((r, index) => {
      const id = r._id || r.placeId || `index-${index}`;
      return selectedIds.has(id);
    });
    
    const bodyData = { leads: selectedLeads };
    if (!skipOptional) {
       if (selectedEmployee) bodyData.assignedUserId = selectedEmployee;
       if (selectedLeadType) bodyData.leadType = selectedLeadType;
       if (selectedLeadSource) bodyData.source = selectedLeadSource;
    }

    try {
      const res = await api('/lead-scrape/import', {
        method: 'POST',
        body: bodyData
      });
      
      if (!res.ok) throw new Error('Import failed');
      
      const data = await res.json();
      setImportMessage(`✅ Successfully imported ${data.count} leads to your CRM!`);
      // Optionally clear selected
      setSelectedIds(new Set());
      setShowImportModal(false);
    } catch (err) {
      setImportMessage('❌ Failed to import leads. Please try again.');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ margin: '0 0 8px', fontSize: '24px', color: 'var(--color-text-primary)' }}>Google Maps Lead Scraper</h1>
        <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>Extract high-quality leads directly from Google Maps and import them to your CRM.</p>
      </div>

      {showSettings && <LeadScraperSettings />}

      {/* Search Card */}
      <div style={{
        background: 'var(--color-background-primary)',
        padding: '24px',
        borderRadius: '12px',
        border: '1px solid var(--color-border-tertiary)',
        marginBottom: '24px'
      }}>
        <form onSubmit={handleScrape} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 120px 150px', gap: '16px', alignItems: 'end' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--color-text-secondary)' }}>
              Search Query
            </label>
            <div style={{ position: 'relative' }}>
              <Search size={18} style={{ position: 'absolute', left: '12px', top: '12px', color: 'var(--color-text-tertiary)' }} />
              <input
                type="text"
                required
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="e.g. Marketing Agencies"
                style={{
                  width: '100%',
                  padding: '12px 16px 12px 40px',
                  border: '1px solid var(--color-border-tertiary)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  background: 'var(--color-background-secondary)',
                  color: 'var(--color-text-primary)'
                }}
              />
            </div>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--color-text-secondary)' }}>
              City / Location
            </label>
            <div style={{ position: 'relative' }}>
              <MapPin size={18} style={{ position: 'absolute', left: '12px', top: '12px', color: 'var(--color-text-tertiary)' }} />
              <input
                type="text"
                required
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="e.g. Mumbai"
                style={{
                  width: '100%',
                  padding: '12px 16px 12px 40px',
                  border: '1px solid var(--color-border-tertiary)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  background: 'var(--color-background-secondary)',
                  color: 'var(--color-text-primary)'
                }}
              />
            </div>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--color-text-secondary)' }}>
              Max Results
            </label>
            <input
              type="number"
              min="10"
              max="200"
              value={maxResults}
              onChange={(e) => setMaxResults(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid var(--color-border-tertiary)',
                borderRadius: '8px',
                fontSize: '14px',
                background: 'var(--color-background-secondary)',
                color: 'var(--color-text-primary)'
              }}
            />
          </div>

          <button
            type="submit"
            disabled={status === 'scraping'}
            style={{
              padding: '12px 20px',
              background: 'var(--color-brand-primary)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: status === 'scraping' ? 'not-allowed' : 'pointer',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              opacity: status === 'scraping' ? 0.7 : 1,
              height: '46px'
            }}
          >
            {status === 'scraping' ? <Loader2 size={18} className="spin" /> : <Search size={18} />}
            {status === 'scraping' ? 'Scraping...' : 'Scrape'}
          </button>
        </form>

        {error && (
          <div style={{ marginTop: '16px', padding: '12px', background: '#FFEBEE', color: '#C62828', borderRadius: '8px', fontSize: '14px' }}>
            {error}
          </div>
        )}
      </div>

      {/* Loading State */}
      {status === 'scraping' && (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--color-text-secondary)' }}>
          <Loader2 size={40} className="spin" style={{ marginBottom: '16px', color: 'var(--color-brand-primary)' }} />
          <h3>Scraping in progress...</h3>
          <p>You can safely leave this page and come back later. We will continue scraping in the background!</p>
        </div>
      )}

      {/* Results Table */}
      {status === 'completed' && results.length > 0 && (
        <div style={{
          background: 'var(--color-background-primary)',
          borderRadius: '12px',
          border: '1px solid var(--color-border-tertiary)',
          overflow: 'hidden',
          marginBottom: '80px' // Space for floating import bar
        }}>
          <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--color-border-tertiary)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: '16px', color: 'var(--color-text-primary)' }}>
              Found {results.length} leads
            </h3>
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
              <button
                onClick={async () => {
                  setResults([]);
                  setSelectedIds(new Set());
                  setQuery('');
                  setLocation('');
                  setMaxResults(20);
                  setStatus('idle');
                  setError('');
                  try {
                    await api('/lead-scrape/cache', { method: 'DELETE' });
                  } catch (err) {
                    console.error('Failed to clear cache', err);
                  }
                }}
                style={{
                  background: 'transparent',
                  border: '1px solid var(--color-border-tertiary)',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  color: 'var(--color-text-secondary)',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500'
                }}
              >
                Clear Results
              </button>
              <button
                onClick={toggleSelectAll}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--color-brand-primary)',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                {selectedIds.size === results.length ? <CheckSquare size={18} /> : <Square size={18} />}
                Select All
              </button>
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead>
                <tr style={{ background: 'var(--color-background-secondary)', color: 'var(--color-text-secondary)', fontSize: '13px', textTransform: 'uppercase' }}>
                  <th style={{ padding: '16px 24px', width: '40px' }}></th>
                  <th style={{ padding: '16px' }}>Business</th>
                  <th style={{ padding: '16px' }}>Contact</th>
                  <th style={{ padding: '16px' }}>Location</th>
                  <th style={{ padding: '16px' }}>Rating</th>
                  <th style={{ padding: '16px' }}>Category</th>
                </tr>
              </thead>
              <tbody>
                {results.map((result, index) => {
                  const id = result._id || result.placeId || `index-${index}`;
                  return (
                  <tr key={id} style={{ borderTop: '1px solid var(--color-border-tertiary)', cursor: 'pointer', background: selectedIds.has(id) ? 'var(--color-background-info)' : 'transparent' }} onClick={() => toggleSelect(id)}>
                    <td style={{ padding: '16px 24px', color: 'var(--color-brand-primary)' }}>
                      {selectedIds.has(id) ? <CheckSquare size={20} /> : <Square size={20} color="var(--color-text-tertiary)" />}
                    </td>
                    <td style={{ padding: '16px' }}>
                      <div style={{ fontWeight: '600', color: 'var(--color-text-primary)', marginBottom: '4px' }}>{result.title}</div>
                      {result.website && (
                        <a href={result.website} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ fontSize: '12px', color: 'var(--color-brand-primary)', display: 'flex', alignItems: 'center', gap: '4px', textDecoration: 'none' }}>
                          <Globe size={12} /> Website
                        </a>
                      )}
                    </td>
                    <td style={{ padding: '16px', fontWeight: '500', color: 'var(--color-text-primary)' }}>
                      <div style={{ marginBottom: '4px' }}>{result.phoneUnformatted || <span style={{ color: 'var(--color-text-tertiary)' }}>No Phone</span>}</div>
                      {(result.emails && result.emails.length > 0) ? (
                        <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>{result.emails[0]}</div>
                      ) : (
                        <div style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>No Email</div>
                      )}
                    </td>
                    <td style={{ padding: '16px', color: 'var(--color-text-secondary)', fontSize: '14px' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                        <MapPin size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
                        <div>
                          <div style={{ fontWeight: '500', color: 'var(--color-text-primary)' }}>{result.city || 'Unknown City'}</div>
                          <div style={{ maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '12px' }}>
                            {result.address}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Star size={16} color="#F59E0B" fill="#F59E0B" />
                        <span style={{ fontWeight: '600' }}>{result.totalScore || '-'}</span>
                        <span style={{ color: 'var(--color-text-tertiary)', fontSize: '12px' }}>({result.reviewsCount || 0})</span>
                      </div>
                    </td>
                    <td style={{ padding: '16px', color: 'var(--color-text-secondary)', fontSize: '14px' }}>
                      {result.categoryName}
                    </td>
                  </tr>
                )})}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Floating Import Bar */}
      {selectedIds.size > 0 && (
        <div style={{
          position: 'fixed',
          bottom: '40px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'var(--color-surface)',
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          padding: '16px 24px',
          borderRadius: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '24px',
          zIndex: 1000,
          border: '1px solid var(--color-border-tertiary)'
        }}>
          <div style={{ fontWeight: '600', color: 'var(--color-text-primary)', fontSize: '16px' }}>
            {selectedIds.size} leads selected
          </div>
          
          <div style={{ width: '1px', height: '24px', background: 'var(--color-border-tertiary)' }}></div>

          {importMessage && !showImportModal ? (
            <div style={{ color: importMessage.includes('❌') ? '#C62828' : '#2E7D32', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '8px' }}>
              {importMessage}
            </div>
          ) : (
            <button
              onClick={() => { setShowImportModal(true); setSelectedEmployee(''); setSelectedLeadType(''); setSelectedLeadSource(''); }}
              disabled={importing}
              style={{
                padding: '12px 24px',
                background: 'var(--color-brand-primary)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                cursor: importing ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                opacity: importing ? 0.7 : 1
              }}
            >
              {importing ? <Loader2 size={18} className="spin" /> : <Download size={18} />}
              {importing ? 'Importing...' : 'Import Selected to CRM'}
            </button>
          )}
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.75)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            background: 'var(--color-surface, #ffffff)',
            padding: '32px',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '500px',
            boxShadow: '0 24px 48px rgba(0,0,0,0.4)',
            position: 'relative'
          }}>
            <button onClick={() => setShowImportModal(false)} style={{
              position: 'absolute', top: '16px', right: '16px',
              background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)'
            }}>
              <X size={24} />
            </button>
            
            <h2 style={{ margin: '0 0 24px 0', fontSize: '20px', color: 'var(--color-text-primary)' }}>
              Import {selectedIds.size} Leads
            </h2>

            {loadingData ? (
              <div style={{ padding: '40px', textAlign: 'center' }}><Loader2 className="spin" size={32} /></div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                
                {/* Assignment */}
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: 'var(--color-text-primary)' }}>
                    <UserIcon size={16} /> Assign to Employee (Optional)
                  </label>
                  <select
                    value={selectedEmployee}
                    onChange={(e) => setSelectedEmployee(e.target.value)}
                    style={{
                      width: '100%', padding: '12px', borderRadius: '8px',
                      border: '1px solid var(--color-border-tertiary)',
                      background: 'var(--color-background-secondary)',
                      color: 'var(--color-text-primary)'
                    }}
                  >
                    <option value="">No Assignment (Skip)</option>
                    {employees.map(emp => (
                      <option key={emp._id} value={emp._id}>{emp.name} ({emp.role})</option>
                    ))}
                  </select>
                </div>

                {/* Lead Type / Temperature */}
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: 'var(--color-text-primary)' }}>
                    <Thermometer size={16} /> Lead Temperature (Optional)
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    <button
                      type="button"
                      onClick={() => setSelectedLeadType('')}
                      style={{
                        padding: '8px 16px', borderRadius: '20px', border: '1px solid var(--color-border-tertiary)',
                        background: selectedLeadType === '' ? 'var(--color-text-primary)' : 'transparent',
                        color: selectedLeadType === '' ? 'var(--color-surface)' : 'var(--color-text-secondary)',
                        cursor: 'pointer', fontWeight: '500'
                      }}
                    >
                      Skip
                    </button>
                    {leadTypes.map(type => (
                      <button
                        key={type._id}
                        type="button"
                        onClick={() => setSelectedLeadType(type.name)}
                        style={{
                          padding: '8px 16px', borderRadius: '20px', 
                          border: `1px solid ${type.color || 'var(--color-border-tertiary)'}`,
                          background: selectedLeadType === type.name ? (type.color || 'var(--color-brand-primary)') : 'transparent',
                          color: selectedLeadType === type.name ? '#fff' : (type.color || 'var(--color-text-secondary)'),
                          cursor: 'pointer', fontWeight: '500'
                        }}
                      >
                        {type.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Lead Source */}
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: 'var(--color-text-primary)' }}>
                    <Globe size={16} /> Lead Source (Optional)
                  </label>
                  <select
                    value={selectedLeadSource}
                    onChange={(e) => setSelectedLeadSource(e.target.value)}
                    style={{
                      width: '100%', padding: '12px', borderRadius: '8px',
                      border: '1px solid var(--color-border-tertiary)',
                      background: 'var(--color-background-secondary)',
                      color: 'var(--color-text-primary)'
                    }}
                  >
                    <option value="">Default (Google Maps)</option>
                    {leadSources.map(source => (
                      <option key={source._id} value={source.name}>{source.name}</option>
                    ))}
                  </select>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                  <button
                    onClick={() => handleImport(false)}
                    disabled={importing}
                    style={{
                      flex: 1, padding: '12px', background: 'var(--color-brand-primary)', color: 'white',
                      border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer',
                      display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px'
                    }}
                  >
                    {importing ? <Loader2 className="spin" size={18} /> : <Download size={18} />}
                    Import Now
                  </button>
                  <button
                    onClick={() => handleImport(true)}
                    disabled={importing}
                    style={{
                      flex: 1, padding: '12px', background: 'transparent', color: 'var(--color-text-secondary)',
                      border: '1px solid var(--color-border-tertiary)', borderRadius: '8px', fontWeight: '600', 
                      cursor: 'pointer'
                    }}
                  >
                    Skip & Import
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { 100% { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default LeadScraperShared;
